/**
 * The single place the application talks to adminwebapi through.
 *
 * The session token lives in memory only. It is short lived and the init data that produced
 * it is available for the whole life of the window, so re-authenticating is cheaper than
 * storing a token where another script on the page could read it.
 */

import { getInitData } from "../telegram/sdk";
import type { AuthResult } from "./types";

const BASE = "/api/v1.0/admin_web";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /**
     * Class name of the exception the internal api raised, when it named one.
     *
     * The internal apis answer with {"code", "exception", "message"} and their messages are
     * english developer prose. The name is what the ui matches on to say the same thing in
     * russian; the message stays as the fallback for anything unmapped.
     */
    readonly exception?: string,
    /**
     * How far a composite action got before it failed, when the route reports one.
     *
     * Only the refund confirmation sends it, and it is there because its two failures mean
     * opposite things and no status code separates them.
     */
    readonly stage?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** No init data at all: opened outside telegram, or a development build without one set. */
export class NotInTelegramError extends Error {
  constructor() {
    super("No telegram init data");
    this.name = "NotInTelegramError";
  }
}

/**
 * The signature was fine and the answer is still no: not on the admin list.
 *
 * Told apart from every other failure because it is the one the application has a screen for.
 */
export class NotAnAdminError extends Error {
  constructor() {
    super("Not an admin");
    this.name = "NotAnAdminError";
  }
}

let token: string | null = null;
let authenticating: Promise<string> | null = null;

async function authenticate(): Promise<string> {
  const initData = getInitData();
  if (!initData) {
    throw new NotInTelegramError();
  }

  const response = await fetch(`${BASE}/auth/telegram`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ init_data: initData }),
  });

  if (!response.ok) {
    // adminwebapi answers 401 for everything: a wrong signature, an old launch and "not an
    // admin" are one answer on purpose, so that probing it tells nobody which it was. From
    // inside the application the launch is real, so 401 means the last of the three
    if (response.status === 401 || response.status === 403) {
      throw new NotAnAdminError();
    }
    const { message, exception } = await readError(response);
    throw new ApiError(response.status, message, exception);
  }

  const result = (await response.json()) as AuthResult;
  token = result.token;
  return result.token;
}

/**
 * Authenticate, but only once at a time.
 *
 * The first screen fires several requests together; without this every one of them would
 * start its own authentication and the last token to arrive would win.
 */
function getToken(): Promise<string> {
  if (token) {
    return Promise.resolve(token);
  }
  authenticating ??= authenticate().finally(() => {
    authenticating = null;
  });
  return authenticating;
}

async function readError(
  response: Response,
): Promise<{ message: string; exception?: string; stage?: string }> {
  try {
    const body = await response.json();
    return {
      message: body?.message ?? body?.error ?? response.statusText,
      exception: typeof body?.exception === "string" ? body.exception : undefined,
      stage: typeof body?.stage === "string" ? body.stage : undefined,
    };
  } catch {
    return { message: response.statusText };
  }
}

function buildQuery(params: Record<string, unknown> | undefined): string {
  if (!params) {
    return "";
  }
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") {
      search.set(key, String(value));
    }
  }
  const query = search.toString();
  return query ? `?${query}` : "";
}

interface RequestOptions {
  method?: string;
  params?: Record<string, unknown>;
  body?: unknown;
  signal?: AbortSignal;
}

async function call<T>(path: string, options: RequestOptions = {}, retryOn401 = true): Promise<T> {
  const { method = "GET", params, body, signal } = options;

  const response = await fetch(`${BASE}${path}${buildQuery(params)}`, {
    method,
    headers: {
      Authorization: `Bearer ${await getToken()}`,
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });

  // tokens are short lived on purpose, so an expired one is the normal course of events and
  // not an error the screens should ever see
  if (response.status === 401 && retryOn401) {
    token = null;
    return call<T>(path, options, false);
  }

  // 403 is never an expired token: it is a valid one whose owner is not on the admin list any
  // more. Retrying would mint the same refusal again
  if (response.status === 403) {
    throw new NotAnAdminError();
  }

  if (!response.ok) {
    const { message, exception, stage } = await readError(response);
    throw new ApiError(response.status, message, exception, stage);
  }

  return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
}

export const api = {
  get: <T>(path: string, params?: Record<string, unknown>, signal?: AbortSignal) =>
    call<T>(path, { params, signal }),
  post: <T>(path: string, body?: unknown, params?: Record<string, unknown>) =>
    call<T>(path, { method: "POST", body, params }),
  put: <T>(path: string, body?: unknown, params?: Record<string, unknown>) =>
    call<T>(path, { method: "PUT", body, params }),
  delete: <T>(path: string, body?: unknown, params?: Record<string, unknown>) =>
    call<T>(path, { method: "DELETE", body, params }),
  /**
   * Drop the session, used when the app wants to start over after an auth failure.
   *
   * The in flight authentication goes too, not only the token. Leaving it would let a request
   * started before the reset hand its token to one started after, which is the opposite of
   * starting over.
   */
  reset: () => {
    token = null;
    authenticating = null;
  },
};
